import { Head, Link } from "@inertiajs/react"
import {
  BarChart,
  Calendar,
  Compass,
  Gift,
  MapPin,
  MessageCircle,
  ShoppingBag,
  Store,
  Users,
} from "lucide-react"
import { useState } from "react"

import FeedItem, {
  type FeedItem as FeedItemType,
} from "@/components/feed/feed-item"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/app-layout"
import {
  eventsPath,
  forumPath,
  marketplacePath,
  marketplaceProductsPath,
  rafflesPath,
} from "@/routes"

interface DiscoverIndexProps {
  trending_events: FeedItemType[]
  featured_businesses: FeedItemType[]
  active_raffles: FeedItemType[]
  recent_forum_posts: FeedItemType[]
  popular_products: FeedItemType[]
  neighborhood_highlights: {
    id: number
    name: string
    description: string
    active_members: number
    recent_activity: number
  }[]
  community_stats: {
    total_members: number
    events_this_month: number
    active_businesses: number
    forum_posts_today: number
  }
}

export default function DiscoverIndex({
  trending_events,
  featured_businesses,
  active_raffles,
  recent_forum_posts,
  popular_products,
  neighborhood_highlights,
  community_stats,
}: DiscoverIndexProps) {
  const [tabValue, setTabValue] = useState<string>("events")

  return (
    <AppLayout title="Descubrir">
      <Head title="Descubrir" />
      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col gap-2">
            <h1 className="text-foreground flex items-center gap-2 text-2xl font-bold md:text-3xl">
              <Compass className="text-primary h-8 w-8 shrink-0" />
              Descubrir
            </h1>
            <p className="text-muted-foreground">
              Explora lo mejor de la comunidad argentina en Madrid.
            </p>
          </div>
          {/* Community Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-0 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="text-primary h-6 w-6 shrink-0" />
                  <div className="text-foreground text-2xl font-bold">
                    {community_stats.events_this_month}
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Eventos este mes
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-0 text-center">
                <div className="flex items-center justify-center gap-2">
                  <Store className="text-primary h-6 w-6 shrink-0" />
                  <div className="text-foreground text-2xl font-bold">
                    {community_stats.active_businesses}
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Negocios activos
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-0 text-center">
                <div className="flex items-center justify-center gap-2">
                  <MessageCircle className="text-primary h-6 w-6 shrink-0" />
                  <div className="text-foreground text-2xl font-bold">
                    {community_stats.forum_posts_today}
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Consultas hoy
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs
            value={tabValue}
            onValueChange={setTabValue}
            className="space-y-6"
          >
            <TabsList className="mb-4 flex h-auto w-full flex-wrap gap-2">
              <TabsTrigger value="events" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Eventos
              </TabsTrigger>
              <TabsTrigger value="raffles" className="flex items-center gap-2">
                <Gift className="h-4 w-4" />
                Sorteos
              </TabsTrigger>
              <TabsTrigger value="forum" className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                Foro
              </TabsTrigger>
              <TabsTrigger
                value="businesses"
                className="flex items-center gap-2"
              >
                <Store className="h-4 w-4" />
                Negocios
              </TabsTrigger>
              {popular_products.length > 0 && (
                <TabsTrigger
                  value="marketplace"
                  className="flex items-center gap-2"
                >
                  <ShoppingBag className="h-4 w-4" />
                  Productos
                </TabsTrigger>
              )}
              <TabsTrigger
                value="neighborhoods"
                className="flex items-center gap-2"
              >
                <MapPin className="h-4 w-4" />
                Barrios
              </TabsTrigger>
            </TabsList>

            {/* Contenido de cada tab */}
            <TabsContent value="events" className="space-y-4">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                  <Calendar className="text-primary h-5 w-5 shrink-0" />
                  Eventos Trending
                </h2>
                <Link href={eventsPath()}>
                  <Button variant="outline" size="sm" className="w-auto">
                    Ver todos
                  </Button>
                </Link>
              </div>
              <div className="space-y-4">
                {trending_events.slice(0, 3).map((event, idx) => (
                  <FeedItem key={idx} item={event} variant="compact" />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="raffles" className="space-y-4">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                  <Gift className="text-primary h-5 w-5 shrink-0" />
                  Sorteos Activos
                </h2>
                <Link href={rafflesPath()}>
                  <Button variant="outline" size="sm" className="w-auto">
                    Ver todos
                  </Button>
                </Link>
              </div>
              <div className="space-y-4">
                {active_raffles.slice(0, 2).map((raffle, idx) => (
                  <FeedItem key={idx} item={raffle} variant="compact" />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="forum" className="space-y-4">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                  <MessageCircle className="text-primary h-5 w-5 shrink-0" />
                  Conversaciones Recientes
                </h2>
                <Link href={forumPath()}>
                  <Button variant="outline" size="sm" className="w-auto">
                    Ver todas
                  </Button>
                </Link>
              </div>
              <div className="space-y-4">
                {recent_forum_posts.slice(0, 3).map((post, idx) => (
                  <FeedItem key={idx} item={post} variant="compact" />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="businesses" className="space-y-6">
              <div className="flex flex-row items-center justify-between gap-2">
                <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                  <Store className="text-primary h-5 w-5 shrink-0" />
                  Negocios Destacados
                </h2>
                <Link href={marketplacePath()}>
                  <Button variant="outline" size="sm" className="w-auto">
                    Ver todos
                  </Button>
                </Link>
              </div>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {featured_businesses.slice(0, 6).map((business, idx) => (
                  <FeedItem key={idx} item={business} variant="card" />
                ))}
              </div>
            </TabsContent>

            {popular_products.length > 0 && (
              <TabsContent value="marketplace" className="space-y-6">
                <div className="flex flex-row items-center justify-between gap-2">
                  <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                    <ShoppingBag className="text-primary h-5 w-5 shrink-0" />
                    Productos Populares
                  </h2>
                  <Link href={marketplaceProductsPath()}>
                    <Button variant="outline" size="sm" className="w-auto">
                      Ver todos
                    </Button>
                  </Link>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {popular_products.slice(0, 8).map((product, idx) => (
                    <FeedItem key={idx} item={product} variant="grid" />
                  ))}
                </div>
              </TabsContent>
            )}

            <TabsContent value="neighborhoods" className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-foreground flex items-center gap-2 text-xl font-semibold">
                  <MapPin className="text-primary h-5 w-5 shrink-0" />
                  Barrios Destacados
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {neighborhood_highlights.map((neighborhood) => (
                  <article
                    key={neighborhood.id}
                    className="hover:bg-muted/30 cursor-pointer px-3 py-3 transition-colors sm:px-4 md:px-6"
                  >
                    <div className="px-1 sm:px-2 md:px-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-foreground text-base font-semibold">
                            {neighborhood.name}
                          </h3>
                          <p className="text-muted-foreground mt-1 line-clamp-3 text-sm">
                            {neighborhood.description}
                          </p>
                          <div className="text-muted-foreground mt-2 flex items-center gap-3 text-xs">
                            {neighborhood.active_members > 0 && (
                              <span className="inline-flex items-center gap-1">
                                <Users className="text-primary h-4 w-4" />
                                {neighborhood.active_members}
                              </span>
                            )}
                            {neighborhood.recent_activity > 0 && (
                              <span className="inline-flex items-center gap-1">
                                <BarChart className="text-primary h-4 w-4" />
                                {neighborhood.recent_activity}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-border/50 -mx-3 mt-3 h-px sm:-mx-4 md:-mx-6" />
                  </article>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          {/* Join Community CTA */}
          {/* <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-blue-500/5">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center justify-center gap-3">
              🇦🇷 Únete a la Comunidad
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Conéctate con miles de argentinos en Madrid. Descubre eventos únicos, 
              encuentra servicios de confianza y forma parte de nuestra gran familia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg">
                <Users className="h-5 w-5 mr-2" />
                Explorar Comunidad
              </Button>
              <Button size="lg" variant="outline">
                <Calendar className="h-5 w-5 mr-2" />
                Ver Próximos Eventos
              </Button>
            </div>
          </CardContent>
        </Card> */}
        </div>
      </div>
    </AppLayout>
  )
}
